import { Link, useLocation } from 'react-router-dom'
import { Calendar } from 'lucide-react'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'

interface NewsCardProps {
  activity: ActivityExplore
  variant: 'featured' | 'compact' | 'inline'
  categoryName?: string
  fill?: boolean
  priority?: boolean
  sizes?: string
}

function formatRelativeDate(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" fill="%23E5E7EB"%3E%3Crect width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="18"%3ENo Image%3C/text%3E%3C/svg%3E'

const IMAGE_CONFIG = {
  featured: {
    width: 1200,
    height: 675,
    sizes: '(min-width: 1280px) 1184px, (min-width: 640px) calc(100vw - 5rem), calc(100vw - 3rem)',
  },
  compact: {
    width: 640,
    height: 400,
    sizes: '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, calc(100vw - 3rem)',
  },
  inline: {
    width: 112,
    height: 112,
    sizes: '112px',
  },
} satisfies Record<NewsCardProps['variant'], { width: number; height: number; sizes: string }>

function resolveNewsCardImage(activity: ActivityExplore, variant: NewsCardProps['variant']): string {
  if (variant === 'inline') {
    return activity.imageThumbnailUrl
      || activity.imageMediumUrl
      || activity.imageWebpUrl
      || activity.image
      || PLACEHOLDER_IMAGE
  }

  if (variant === 'compact') {
    return activity.imageMediumUrl
      || activity.imageThumbnailUrl
      || activity.imageWebpUrl
      || activity.image
      || PLACEHOLDER_IMAGE
  }

  return activity.imageLargeUrl
    || activity.imageMediumUrl
    || activity.imageWebpUrl
    || activity.image
    || PLACEHOLDER_IMAGE
}

function buildNewsCardSrcSet(activity: ActivityExplore): string | undefined {
  const entries = [
    activity.imageThumbnailUrl ? `${activity.imageThumbnailUrl} 160w` : '',
    activity.imageMediumUrl ? `${activity.imageMediumUrl} 640w` : '',
    activity.imageLargeUrl ? `${activity.imageLargeUrl} 1200w` : '',
  ].filter(Boolean)

  return entries.length > 0 ? entries.join(', ') : undefined
}

function NewsCardMetadata({
  activity,
  className,
  showIcon = false,
}: {
  activity: ActivityExplore
  className: string
  showIcon?: boolean
}) {
  if (!activity.publishTime && !activity.belongsToUser?.name) {
    return null
  }

  return (
    <div className={className}>
      {activity.publishTime && (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          {showIcon && <Calendar className="size-3.5 shrink-0" aria-hidden="true" />}
          <time>{formatRelativeDate(activity.publishTime)}</time>
        </span>
      )}
      {activity.belongsToUser?.name && (
        <span className="min-w-0 wrap-break-word">
          <span aria-hidden="true">· </span>
          {activity.belongsToUser.name}
        </span>
      )}
    </div>
  )
}

export default function NewsCard({
  activity,
  variant,
  categoryName,
  fill = false,
  priority = false,
  sizes,
}: NewsCardProps) {
  const location = useLocation()
  const screenPath = `${location.pathname}${location.search || ''}`
  const displayCategory = categoryName ?? activity.newsCategoryString ?? ''
  const image = resolveNewsCardImage(activity, variant)
  const srcSet = buildNewsCardSrcSet(activity)
  const imageConfig = IMAGE_CONFIG[variant]
  const imageSizes = sizes ?? imageConfig.sizes
  const loading = priority ? 'eager' : 'lazy'
  const fetchPriority = priority ? 'high' : 'low'

  if (variant === 'featured') {
    return (
      <Link
        to={`/news/${activity.uid}`}
        onClick={() => {
          AnalyticsFeature.trackContentOpen(screenPath, 'news', activity.uid, 'news_card')
        }}
        className={`group relative block overflow-hidden rounded-2xl no-underline shadow-lg shadow-black/20 ring-1 ring-black/10 transition-shadow hover:shadow-lg hover:shadow-black/30 ${fill ? 'h-full' : ''}`}
      >
        <div className={`relative w-full overflow-hidden bg-border-light ${fill ? 'h-full' : 'aspect-video'}`}>
          <img
            src={image}
            srcSet={srcSet}
            sizes={imageSizes}
            alt={activity.title ?? 'News article'}
            width={imageConfig.width}
            height={imageConfig.height}
            className="absolute inset-0 block h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading={loading}
            fetchPriority={fetchPriority}
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {displayCategory && (
            <span className="mb-2 inline-block rounded-full bg-blue-light px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-blue">
              {displayCategory}
            </span>
          )}
          <h3 className="text-xl font-bold leading-tight text-white sm:text-2xl">
            {activity.title}
          </h3>
          <NewsCardMetadata
            activity={activity}
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/80"
          />
        </div>
      </Link>
    )
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/news/${activity.uid}`}
        onClick={() => {
          AnalyticsFeature.trackContentOpen(screenPath, 'news', activity.uid, 'news_card')
        }}
        className={`group flex flex-col overflow-hidden rounded-xl bg-card-surface no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-shadow hover:shadow-lg hover:shadow-black/30 ${fill ? 'h-full' : ''}`}
      >
        <div className={`relative w-full overflow-hidden bg-border-light ${fill ? 'min-h-0 flex-1' : 'aspect-16/10'}`}>
          <img
            src={image}
            srcSet={srcSet}
            sizes={imageSizes}
            alt={activity.title ?? 'News article'}
            width={imageConfig.width}
            height={imageConfig.height}
            className="absolute inset-0 block h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading={loading}
            fetchPriority={fetchPriority}
            decoding="async"
          />
        </div>
        <div className="shrink-0 p-4">
          {displayCategory && (
            <span className="mb-2 inline-block rounded-full bg-blue-light px-2.5 py-0.5 text-xs font-bold text-blue">
              {displayCategory}
            </span>
          )}
          <h3 className="line-clamp-2 text-lg font-bold text-text-primary group-hover:text-primary">
            {activity.title}
          </h3>
          <NewsCardMetadata
            activity={activity}
            className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted"
          />
        </div>
      </Link>
    )
  }

  return (
    <Link
      to={`/news/${activity.uid}`}
      onClick={() => {
        AnalyticsFeature.trackContentOpen(screenPath, 'news', activity.uid, 'news_card')
      }}
      className="group flex items-start gap-3 overflow-hidden rounded-xl bg-card-surface p-3 no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/30"
    >
      <div className="flex min-h-24 min-w-0 flex-1 flex-col gap-2 sm:min-h-28">
        <div>
          {displayCategory && (
            <span className="mb-1.5 inline-block rounded-full bg-blue-light px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-blue">
              {displayCategory}
            </span>
          )}
          <h3 className="line-clamp-2 text-sm font-bold leading-tight text-text-primary group-hover:text-primary lg:text-base">
            {activity.title}
          </h3>
        </div>
        <NewsCardMetadata
          activity={activity}
          showIcon
          className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted"
        />
      </div>
      <div className="relative size-24 shrink-0 overflow-hidden rounded-lg bg-border-light sm:size-28">
        <img
          src={image}
          srcSet={srcSet}
          sizes={imageSizes}
          alt={activity.title ?? 'News article'}
          width={imageConfig.width}
          height={imageConfig.height}
          className="absolute inset-0 block h-full w-full object-cover"
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
        />
      </div>
    </Link>
  )
}
